import type { ContactRequirement } from './config';
import type { ErrorCode } from './errors';
import type { FilterGroup, SortSpec } from './filters';

/** Workspace roles, lowest to highest privilege. */
export const ROLES = ['viewer', 'member', 'admin', 'owner'] as const;
export type Role = (typeof ROLES)[number];

export function roleAtLeast(role: Role, minimum: Role): boolean {
  return ROLES.indexOf(role) >= ROLES.indexOf(minimum);
}

export type WorkspaceKind = 'production' | 'demo';

export type AiProviderSetting = 'auto' | 'anthropic' | 'gemini' | 'workers_ai' | 'off';

export interface WorkspaceSettings {
  ai?: {
    provider?: AiProviderSetting;
    model?: string;
    /** Soft monthly budget; AI calls are skipped once reached. */
    monthlyBudgetUsd?: number;
  };
  defaults?: {
    contactRequirement?: ContactRequirement;
    country?: string;
  };
}

export type ProjectStatus = 'draft' | 'queued' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
export type JobStatus = 'queued' | 'running' | 'paused' | 'cancelling' | 'cancelled' | 'completed' | 'failed';

export const JOB_PHASES = ['acquire', 'merge', 'enrich', 'ai', 'finalize', 'done'] as const;
export type JobPhase = (typeof JOB_PHASES)[number];

export const JOB_PHASE_LABELS: Record<JobPhase, string> = {
  acquire: 'Discovery',
  merge: 'Merging',
  enrich: 'Enrichment',
  ai: 'Custom fields',
  finalize: 'Filtering & finalizing',
  done: 'Done',
};

export const TASK_KINDS = [
  'discover',
  'detail',
  'merge',
  'enrich_website',
  'enrich_linkedin',
  'people',
  'ai_fields',
  'finalize',
] as const;
export type TaskKind = (typeof TASK_KINDS)[number];
export type TaskStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped' | 'cancelled';

export interface ProjectStats {
  accounts: number;
  discarded: number;
  people: number;
  withPhone: number;
  withEmail: number;
  contactable: number;
  sourceRecords: number;
  duplicatesPending: number;
  updatedAt: number;
}

export function emptyProjectStats(now = Date.now()): ProjectStats {
  return {
    accounts: 0,
    discarded: 0,
    people: 0,
    withPhone: 0,
    withEmail: 0,
    contactable: 0,
    sourceRecords: 0,
    duplicatesPending: 0,
    updatedAt: now,
  };
}

export interface JobUsage {
  requests: number;
  browserRenders: number;
  placesRequests: number;
  aiCalls: number;
  aiInputTokens: number;
  aiOutputTokens: number;
  /** Approximate spend by category, USD. Estimates only — check provider billing for exact cost. */
  costUsd: { places: number; ai: number; browser: number };
}

export function emptyUsage(): JobUsage {
  return {
    requests: 0,
    browserRenders: 0,
    placesRequests: 0,
    aiCalls: 0,
    aiInputTokens: 0,
    aiOutputTokens: 0,
    costUsd: { places: 0, ai: 0, browser: 0 },
  };
}

export function addUsage(target: JobUsage, delta: Partial<JobUsage>): JobUsage {
  return {
    requests: target.requests + (delta.requests ?? 0),
    browserRenders: target.browserRenders + (delta.browserRenders ?? 0),
    placesRequests: target.placesRequests + (delta.placesRequests ?? 0),
    aiCalls: target.aiCalls + (delta.aiCalls ?? 0),
    aiInputTokens: target.aiInputTokens + (delta.aiInputTokens ?? 0),
    aiOutputTokens: target.aiOutputTokens + (delta.aiOutputTokens ?? 0),
    costUsd: {
      places: target.costUsd.places + (delta.costUsd?.places ?? 0),
      ai: target.costUsd.ai + (delta.costUsd?.ai ?? 0),
      browser: target.costUsd.browser + (delta.costUsd?.browser ?? 0),
    },
  };
}

export interface SourceProgress {
  source: string;
  label: string;
  target: number;
  discovered: number;
  failedTasks: number;
  pendingTasks: number;
  status: 'pending' | 'running' | 'done' | 'failed';
  lastError?: { code: ErrorCode; message: string };
}

/** Live job progress snapshot broadcast by the JobCoordinator Durable Object. */
export interface JobProgress {
  jobId: string;
  projectId: string;
  status: JobStatus;
  phase: JobPhase;
  runNumber: number;
  createdAt: number;
  startedAt: number | null;
  finishedAt: number | null;
  updatedAt: number;
  discovery: SourceProgress[];
  enrichment: {
    websitesTotal: number;
    websitesVisited: number;
    websitesFailed: number;
    linkedinTotal: number;
    linkedinVisited: number;
    peopleFound: number;
  };
  contacts: { accountsWithEmail: number; accountsWithPhone: number; emailsFound: number; phonesFound: number };
  merging: { sourceRecords: number; mergedRecords: number; autoMerged: number; potentialDuplicates: number; accounts: number };
  ai: { total: number; done: number; aiCalls: number };
  filtering: { discardedNoContact: number; discardedOther: number; finalAccounts: number };
  tasks: Record<TaskStatus, number>;
  errors: Array<{ code: ErrorCode; title: string; count: number; sample?: string }>;
  usage: JobUsage;
}

export type ExportFormat = 'csv' | 'xlsx' | 'google_sheets';
export type ExportStatus = 'queued' | 'running' | 'completed' | 'failed' | 'expired';

export const EXPORT_TRANSFORMS = [
  'none',
  'uppercase',
  'lowercase',
  'first',
  'join_comma',
  'join_semicolon',
  'join_newline',
  'yes_no',
  'country_name',
  'date_iso',
  'phone_e164',
  'phone_national',
] as const;
export type ExportTransform = (typeof EXPORT_TRANSFORMS)[number];

export interface ExportColumn {
  /** Field id (see account field registry), e.g. "name", "phone", "attr:google_maps.rating", "custom.hiring". */
  field: string;
  header: string;
  transform?: ExportTransform;
  defaultValue?: string;
}

export interface ExportOptions {
  /** UTF-8 BOM so Excel opens Arabic text correctly. */
  includeBom: boolean;
  delimiter: ',' | ';' | '\t';
  listSeparator: string;
}

export const DEFAULT_EXPORT_OPTIONS: ExportOptions = { includeBom: true, delimiter: ',', listSeparator: '; ' };

export interface ExportScope {
  projectId?: string;
  filter?: FilterGroup;
  search?: string;
  sort?: SortSpec[];
  ids?: string[];
  status: 'active' | 'discarded' | 'all';
  sources?: string[];
  tags?: string[];
}

export interface ExportRequestSnapshot {
  scope: ExportScope;
  columns: ExportColumn[];
  options: ExportOptions;
  sheet?: { spreadsheetId: string; tabName: string };
}

export interface ConnectionMetadata {
  /** Non-sensitive hint for the UI, e.g. "AIza…4f2Q" or the service account email. */
  hint?: string;
  accountLabel?: string;
  details?: Record<string, string>;
}

export type ConnectionStatus = 'connected' | 'invalid' | 'expired' | 'revoked';

export interface DuplicateReason {
  code:
    | 'same_domain'
    | 'same_phone'
    | 'same_email'
    | 'same_social'
    | 'name_similarity'
    | 'geo_proximity'
    | 'address_similarity'
    | 'city_conflict'
    | 'name_conflict'
    | 'same_source';
  label: string;
}

export interface SavedViewConfig {
  columns: Array<{ id: string; width?: number; visible: boolean }>;
  sort: SortSpec[];
  filter?: FilterGroup;
  search?: string;
  status?: 'active' | 'discarded' | 'all';
}

export interface AdapterFieldRates {
  /** attribute key -> share of records (0..1) that had a value */
  [attribute: string]: number;
}

export interface SourceHealthAlert {
  kind: 'field_drop' | 'zero_results' | 'high_failure' | 'blocked';
  attribute?: string;
  message: string;
  previous?: number;
  current?: number;
}
